import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiPlus,
  FiDownload,
  FiSearch,
  FiFilter,
  FiGrid,
  FiList,
  FiMapPin,
  FiBriefcase,
  FiCalendar,
  FiPhone,
  FiMail,
  FiEdit2,
  FiUser,
  FiFileText,
  FiDollarSign,
  FiStar,
  FiMoreHorizontal,
  FiHome,
  FiChevronDown,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { useAdminListOverlay } from '../../../utils/useAdminPageSearch';
import AddMember from './AddMember';
import EditMemberModal from './EditMemberModal';
import {
  hasAnyRole,
  workforceRoleLabel,
} from '../utils/workforcePeople';
import '../styles/EmployeeDirectory.css';

const USERS_QUERY = `
  query EmployeeDirectoryUsers($page: Int, $limit: Int) {
    getAllUser(page: $page, limit: $limit) {
      data {
        id
        firstName
        lastName
        email
        contact
        address
        roles
        employeeCode
        jobTitle
        typeOfWork
        salaryTerm
        perHourRate
        payType
        paydayDay
        paydayTime
        isActive
        branchId
        joiningDate
        profileComplete
        upload
      }
      total
      totalPages
      page
    }
  }
`;

const TIME_CARDS_QUERY = `
  query DirectoryTimeCards($date: String) {
    findTimeCards(date: $date) {
      id
      userId
      date
      timeIn
      timeOut
      leaveType
      duration
      notes
      user { id firstName lastName employeeCode }
    }
  }
`;

const BRANCHES_QUERY = `
  query DirectoryBranches {
    getMyCompanyBranches {
      id
      name
      location
    }
  }
`;

const PROJECTS_QUERY = `
  query DirectoryProjects {
    findAllProject {
      id
      name
      startDate
      deadLine
      startTime
      endTime
      status
      manager { id }
      booking { id address fullName }
      tasks {
        id
        title
        location
        date
        time
        user { id }
      }
    }
  }
`;

const LEAVE_QUERY = `
  query DirectoryLeaveRequests {
    leaveRequests {
      id
      name
      employeeId
      status
      startDate
      endDate
    }
  }
`;

const DOCS_QUERY = `
  query DirectoryDocs {
    getWorkforceDocuments {
      id
      userId
      employeeCode
      employeeName
      documentType
    }
  }
`;

const AVATARS = [
  '#60a5fa',
  '#34d399',
  '#a78bfa',
  '#f472b6',
  '#fb923c',
  '#38bdf8',
  '#fbbf24',
  '#2dd4bf',
];

const PAGE_SIZE = 12;

function initials(name) {
  return (
    String(name || '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() || '')
      .join('') || '?'
  );
}

function avatarColor(key) {
  let h = 0;
  const s = String(key || '');
  for (let i = 0; i < s.length; i += 1) h = (h + s.charCodeAt(i) * 17) % AVATARS.length;
  return AVATARS[h];
}

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function cardDateISO(value) {
  if (!value) return '';
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatJoiningDate(value) {
  const iso = cardDateISO(value);
  if (!iso) return '—';
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function isOnLeaveToday(leave) {
  if (String(leave.status || '').toLowerCase() !== 'approved') return false;
  const start = leave.startDate ? new Date(leave.startDate) : null;
  const end = leave.endDate ? new Date(leave.endDate) : start;
  if (!start || Number.isNaN(start.getTime())) return false;
  const now = new Date();
  now.setHours(12, 0, 0, 0);
  const s = new Date(start);
  s.setHours(0, 0, 0, 0);
  const e = new Date(end && !Number.isNaN(end.getTime()) ? end : start);
  e.setHours(23, 59, 59, 999);
  return now >= s && now <= e;
}

// The DB stores UserRole *values* ("Super Admin", "Scheduler/Dispatcher"), not the
// GraphQL enum keys, so match through the shared normaliser rather than raw
// substring tests — "SUPER ADMIN" never contains "SUPER_ADMIN".
function roleLabel(roles = []) {
  return workforceRoleLabel(roles);
}

function employmentTypeOf(user) {
  if (hasAnyRole(user.roles || [], ['SCHEDULER_DISPATCHER', 'CONTRACTOR'])) {
    return 'Contractor';
  }
  const raw = `${user.typeOfWork || ''} ${user.salaryTerm || ''}`.toLowerCase();
  if (raw.includes('casual')) return 'Casual';
  if (raw.includes('part')) return 'Casual';
  if (raw.includes('contract')) return 'Contractor';
  if (raw.includes('full')) return 'Full-time';
  return 'Full-time';
}

function titleOf(user) {
  return roleLabel(user.roles);
}

function memberTitleForModal(user) {
  return roleLabel(user.roles);
}

function pctColor(n) {
  if (n >= 90) return 'good';
  if (n >= 75) return 'mid';
  return 'low';
}

function scoreFromSeed(id, docsPct, compliancePct) {
  if (docsPct != null && compliancePct != null) {
    return Math.round((docsPct + compliancePct) / 2);
  }
  let h = 0;
  const s = String(id || '');
  for (let i = 0; i < s.length; i += 1) h = (h + s.charCodeAt(i) * 13) % 28;
  return 72 + h;
}

/** Normalize DB/GraphQL time values to HH:MM */
function formatClock(value) {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  const match = s.match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return `${String(match[1]).padStart(2, '0')}:${match[2]}`;
}

function isOpenClock(card) {
  if (!card?.timeIn) return false;
  const notes = String(card.notes || '');
  if (notes.includes('[CLOCK_OPEN]')) return true;
  const inn = formatClock(card.timeIn);
  const out = formatClock(card.timeOut);
  // Employee portal open shift: timeOut mirrors timeIn until clock-out
  return Boolean(inn && out && inn === out);
}

function formatShiftFromCard(card) {
  const inn = formatClock(card?.timeIn);
  if (!inn) return null;
  if (isOpenClock(card)) return `${inn}-…`;
  const out = formatClock(card?.timeOut);
  return out ? `${inn}-${out}` : `${inn}-…`;
}

function formatShiftFromProject(project) {
  const start = formatClock(project?.startTime);
  const end = formatClock(project?.endTime);
  if (start && end) return `${start}-${end}`;
  if (start) return `${start}-…`;
  return null;
}

function formatShiftFromTask(task) {
  if (!task?.time) return null;
  const raw = String(task.time).trim();
  if (raw.includes('-')) {
    const parts = raw.split('-').map((p) => formatClock(p.trim()) || p.trim());
    if (parts.length >= 2) return `${parts[0]}-${parts[1]}`;
  }
  const single = formatClock(raw);
  return single || raw;
}

/** Prefer suburb/city from "street, cityState, postcode" */
function locationFromAddress(address) {
  if (!address) return null;
  const parts = String(address)
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length >= 2) return parts[1];
  if (parts.length === 1 && parts[0].length <= 64) return parts[0];
  return parts[0]?.slice(0, 64) || null;
}

function resolveBranchLabel(user, branchById, branches) {
  const branch = user.branchId ? branchById.get(user.branchId) : null;
  if (branch?.name) return branch.name;
  if (branch?.location) return branch.location;
  const fromAddress = locationFromAddress(user.address);
  if (fromAddress) return fromAddress;
  if (user.address && String(user.address).trim()) {
    return String(user.address).trim();
  }
  if (Array.isArray(branches) && branches.length === 1) {
    return branches[0].name || branches[0].location || null;
  }
  if (Array.isArray(branches) && branches.length > 1) {
    const active = branches.find((b) => b.name);
    if (active?.name) return active.name;
  }
  return null;
}

function resolveSite(assignment) {
  if (!assignment) return null;
  return (
    (assignment.taskLocation && String(assignment.taskLocation).trim()) ||
    (assignment.bookingAddress &&
      locationFromAddress(assignment.bookingAddress)) ||
    (assignment.bookingAddress && String(assignment.bookingAddress).trim()) ||
    (assignment.projectName && String(assignment.projectName).trim()) ||
    (assignment.taskTitle && String(assignment.taskTitle).trim()) ||
    null
  );
}

function projectActiveOn(project, today) {
  const status = String(project?.status || '').toLowerCase();
  if (status.includes('inactive') || status.includes('complete') || status.includes('cancel')) {
    return false;
  }
  const start = project?.startDate ? cardDateISO(project.startDate) : '';
  const end = project?.deadLine ? cardDateISO(project.deadLine) : '';
  if (start && today < start) return false;
  if (end && today > end) return false;
  return true;
}

/** Build per-user project/task assignment for site + scheduled shift */
function buildAssignmentsByUser(projects, today) {
  const map = new Map();

  const upsert = (userId, payload, score) => {
    if (!userId) return;
    const prev = map.get(userId);
    if (!prev || score > prev.score) {
      map.set(userId, { ...payload, score });
    }
  };

  (projects || []).forEach((project) => {
    if (!projectActiveOn(project, today)) return;
    const bookingAddress = project.booking?.address || '';
    const projectName = project.name || '';
    const shift = formatShiftFromProject(project);
    const base = {
      projectName,
      bookingAddress,
      startTime: project.startTime,
      endTime: project.endTime,
      shift,
    };

    if (project.manager?.id) {
      upsert(project.manager.id, { ...base, taskLocation: null, taskTitle: null }, 2);
    }

    (project.tasks || []).forEach((task) => {
      const uid = task.user?.id;
      if (!uid) return;
      // Prefer tasks dated today; otherwise keep any open project task
      const isToday = !task.date || sameDay(task.date, today);
      const taskShift = formatShiftFromTask(task) || shift;
      upsert(
        uid,
        {
          ...base,
          shift: taskShift,
          taskLocation: task.location || null,
          taskTitle: task.title || null,
        },
        isToday ? 5 : 3,
      );
    });
  });

  return map;
}

/** Collect all active project/task jobs for each user */
function buildJobsByUser(projects, today) {
  const map = new Map();

  const push = (userId, job) => {
    if (!userId) return;
    const list = map.get(userId) || [];
    const key = `${job.projectId || ''}|${job.taskId || ''}|${job.title}`;
    if (list.some((j) => j.key === key)) return;
    list.push({ ...job, key });
    map.set(userId, list);
  };

  (projects || []).forEach((project) => {
    if (!projectActiveOn(project, today)) return;
    const bookingAddress = project.booking?.address || '';
    const client = project.booking?.fullName || '';
    const shift = formatShiftFromProject(project);

    if (project.manager?.id) {
      push(project.manager.id, {
        projectId: project.id,
        taskId: null,
        title: project.name || 'Project',
        role: 'Manager',
        location: bookingAddress || '—',
        client: client || '—',
        shift: shift || '—',
        status: project.status || 'Active',
      });
    }

    (project.tasks || []).forEach((task) => {
      const uid = task.user?.id;
      if (!uid) return;
      push(uid, {
        projectId: project.id,
        taskId: task.id,
        title: task.title || project.name || 'Task',
        role: 'Assigned',
        location: task.location || bookingAddress || '—',
        client: client || '—',
        shift: formatShiftFromTask(task) || shift || '—',
        status: project.status || 'Active',
        date: task.date || null,
      });
    });
  });

  return map;
}

function formatMoney(value) {
  const n = Number(value);
  if (value == null || value === '' || Number.isNaN(n)) return '—';
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: n % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

function lateLabel(card) {
  if (!card?.timeIn) return null;
  const leave = String(card.leaveType || '').toLowerCase();
  if (leave.includes('late')) return 'Late';
  return null;
}

function sameDay(taskDate, today) {
  if (!taskDate) return false;
  const raw = String(taskDate).trim();
  if (raw.startsWith(today)) return true;
  return cardDateISO(raw) === today;
}

async function fetchAllUsers() {
  const all = [];
  let page = 1;
  let totalPages = 1;
  while (page <= totalPages) {
    let data;
    try {
      data = await graphqlRequest(USERS_QUERY, { page, limit: 100 });
    } catch (err) {
      const msg = String(err?.message || '').toLowerCase();
      let query = USERS_QUERY;
      if (msg.includes('branchid')) {
        query = query.replace(/\s*branchId\s*/, '\n');
      }
      if (msg.includes('profilecomplete')) {
        query = query.replace(/\s*profileComplete\s*/, '\n');
      }
      if (msg.includes('upload')) {
        query = query.replace(/\s*upload\s*/, '\n');
      }
      if (query !== USERS_QUERY) {
        data = await graphqlRequest(query, { page, limit: 100 });
      } else {
        throw err;
      }
    }
    const result = data?.getAllUser;
    totalPages = result?.totalPages || 1;
    all.push(...(result?.data || []));
    page += 1;
  }
  return all;
}

async function fetchTodayTimeCards(date) {
  // Only today's cards — clocked-in must match employee-portal clock-ins for today.
  const dated = await graphqlRequest(TIME_CARDS_QUERY, { date }).catch(() => ({
    findTimeCards: [],
  }));
  return (dated?.findTimeCards || []).filter((c) => {
    const cardDay = cardDateISO(c.date);
    return !cardDay || cardDay === date;
  });
}

// Roles that can be created from this page. Each drives a different Add Member
// wizard (Contractor skips Superannuation and swaps TFN/visa fields for ABN/GST).
const ADD_MEMBER_TYPES = [
  { type: 'Cleaner', label: 'Cleaner', hint: 'Field staff' },
  { type: 'Manager', label: 'Manager', hint: 'Operations manager' },
  { type: 'Admin', label: 'Admin', hint: 'Portal administrator' },
  { type: 'Contractor', label: 'Contractor', hint: 'ABN / subcontractor' },
];
const ADD_MEMBER_TYPE_VALUES = ADD_MEMBER_TYPES.map((t) => t.type);

function shapeDirectoryEditMember(emp) {
  if (!emp) return null;
  return {
    id: emp.code,
    dbId: emp.dbId,
    name: emp.name,
    title: emp.modalTitle,
    email: emp.email,
    contact: emp.contact,
    code: emp.code,
    photo: emp.photo || '',
  };
}

export default function EmployeeDirectory() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('grid');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const addMenuRef = useRef(null);
  const [menuId, setMenuId] = useState(null);
  const [infoModal, setInfoModal] = useState(null);
  const overlay = useAdminListOverlay(employees, {
    idKey: 'dbId',
    extraClearKeys: ['type'],
  });
  const addType = ADD_MEMBER_TYPE_VALUES.includes(overlay.get('type'))
    ? overlay.get('type')
    : 'Cleaner';
  const showAdd = overlay.showAdd;
  const editMember = shapeDirectoryEditMember(overlay.editing);

  const load = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const date = todayISO();
      const [users, cards, leaveData, docsData, branchesData, projectsData] =
        await Promise.all([
          fetchAllUsers(),
          fetchTodayTimeCards(date),
          graphqlRequest(LEAVE_QUERY).catch(() => ({ leaveRequests: [] })),
          graphqlRequest(DOCS_QUERY).catch((err) => {
            console.error('Failed to load workforce documents:', err);
            if (showLoader) {
              toast.error(err?.message || 'Failed to load employee documents.');
            }
            return { getWorkforceDocuments: [] };
          }),
          graphqlRequest(BRANCHES_QUERY).catch(() => ({
            getMyCompanyBranches: [],
          })),
          graphqlRequest(PROJECTS_QUERY).catch(() => ({
            findAllProject: [],
          })),
        ]);

      const branches = branchesData?.getMyCompanyBranches || [];
      const branchById = new Map();
      branches.forEach((b) => {
        if (b?.id) branchById.set(b.id, b);
      });

      const cardByUser = new Map();
      (cards || []).forEach((c) => {
        if (!c.userId) return;
        const prev = cardByUser.get(c.userId);
        // Prefer an open (clocked-in) card when multiple exist today
        if (!prev || isOpenClock(c)) cardByUser.set(c.userId, c);
      });

      const assignmentByUser = buildAssignmentsByUser(
        projectsData?.findAllProject || [],
        date,
      );
      const jobsByUser = buildJobsByUser(
        projectsData?.findAllProject || [],
        date,
      );

      const leaves = (leaveData?.leaveRequests || []).filter(isOnLeaveToday);
      const leaveKeys = new Set(
        leaves.flatMap((l) => [
          String(l.employeeId || '').toLowerCase(),
          String(l.name || '').toLowerCase(),
        ]),
      );

      const docs = Array.isArray(docsData?.getWorkforceDocuments)
        ? docsData.getWorkforceDocuments
        : [];
      const docsByUser = new Map();
      const bumpDoc = (key) => {
        if (!key) return;
        docsByUser.set(key, (docsByUser.get(key) || 0) + 1);
      };
      docs.forEach((d) => {
        bumpDoc(d.userId);
        bumpDoc(String(d.employeeCode || '').trim().toUpperCase());
        bumpDoc(String(d.employeeName || '').trim().toLowerCase());
      });

      const mapped = users.map((u) => {
        const name =
          `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown';
        const codeKey = String(u.employeeCode || '').trim().toUpperCase();
        const onLeave =
          leaveKeys.has(String(u.id).toLowerCase()) ||
          leaveKeys.has(name.toLowerCase()) ||
          leaveKeys.has(String(u.employeeCode || '').toLowerCase());
        const card = cardByUser.get(u.id);
        const assignment = assignmentByUser.get(u.id);
        const late = lateLabel(card);
        const open = isOpenClock(card);

        // Attendance for today — keep labels clock-focused (not employment "Active").
        let todayStatus = 'not_clocked_in';
        if (onLeave) todayStatus = 'on_leave';
        else if (late && open) todayStatus = 'late';
        else if (open) todayStatus = 'clocked_in';
        else if (card?.timeIn) todayStatus = 'clocked_out';

        const empStatus = onLeave
          ? 'on_leave'
          : u.isActive === false
            ? 'inactive'
            : 'active';

        const docCount =
          docsByUser.get(u.id) ||
          docsByUser.get(codeKey) ||
          docsByUser.get(name.toLowerCase()) ||
          0;
        const docsPct =
          docCount > 0 ? Math.min(100, Math.round((docCount / 4) * 100)) : 0;
        const compliancePct =
          docCount > 0 ? Math.min(100, docsPct + 4) : 0;
        const score =
          docCount > 0
            ? Math.round((docsPct + compliancePct) / 2)
            : scoreFromSeed(u.id);

        const shiftFromCard = formatShiftFromCard(card);
        const shiftFromAssignment = assignment?.shift || null;
        const shift = late
          ? `Late${shiftFromCard ? ` ${shiftFromCard}` : ''}`
          : shiftFromCard || shiftFromAssignment || '—';

        const siteFromAssignment = resolveSite(assignment);
        const site =
          siteFromAssignment ||
          locationFromAddress(u.address) ||
          '—';

        const branch =
          resolveBranchLabel(u, branchById, branches) ||
          locationFromAddress(assignment?.bookingAddress) ||
          '—';
        const empType = employmentTypeOf(u);
        const jobs = jobsByUser.get(u.id) || [];

        return {
          id: u.id,
          dbId: u.id,
          name,
          code:
            u.employeeCode || `EMP-${String(u.id).slice(0, 4).toUpperCase()}`,
          title: titleOf(u),
          modalTitle: memberTitleForModal(u),
          email: u.email || '—',
          contact: u.contact || '—',
          branch,
          employmentType: empType,
          shift,
          joiningDate: formatJoiningDate(u.joiningDate),
          lateNote: late ? 'Late' : null,
          site,
          empStatus,
          todayStatus,
          docsPct,
          compliancePct,
          score,
          avatar: avatarColor(name),
          roles: u.roles || [],
          jobTitle: u.jobTitle || '',
          typeOfWork: u.typeOfWork || empType,
          salaryTerm: u.salaryTerm || '—',
          perHourRate: u.perHourRate,
          payType: u.payType || '—',
          paydayDay: u.paydayDay || '—',
          paydayTime: u.paydayTime || '—',
          profileComplete: u.profileComplete !== false,
          photo: u.upload || '',
          jobs,
        };
      });

      setEmployees(mapped);
      setPage(1);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to load employees.');
      setEmployees([]);
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
  }, [load]);

  // Close the Add Employee role menu on outside click / Escape
  useEffect(() => {
    if (!addMenuOpen) return undefined;
    const onPointerDown = (e) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) {
        setAddMenuOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setAddMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [addMenuOpen]);

  // Pick up profile / document changes from the employee portal
  useEffect(() => {
    const refreshQuietly = () => {
      void load(false);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshQuietly();
    };
    window.addEventListener('focus', refreshQuietly);
    window.addEventListener('workforce-documents-refresh', refreshQuietly);
    window.addEventListener('admin-backend-notifications-refresh', refreshQuietly);
    document.addEventListener('visibilitychange', onVisibility);
    const intervalId = window.setInterval(refreshQuietly, 30000);
    return () => {
      window.removeEventListener('focus', refreshQuietly);
      window.removeEventListener('workforce-documents-refresh', refreshQuietly);
      window.removeEventListener(
        'admin-backend-notifications-refresh',
        refreshQuietly,
      );
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(intervalId);
    };
  }, [load]);

  const stats = useMemo(() => {
    const total = employees.length;
    const clockedIn = employees.filter((e) => e.todayStatus === 'clocked_in').length;
    const onLeave = employees.filter((e) => e.todayStatus === 'on_leave').length;
    const notClockedIn = employees.filter(
      (e) => e.todayStatus === 'not_clocked_in' || e.todayStatus === 'clocked_out',
    ).length;
    const late = employees.filter((e) => e.todayStatus === 'late').length;
    return { total, clockedIn, onLeave, notClockedIn, late };
  }, [employees]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter((e) => {
      if (typeFilter !== 'All' && e.employmentType !== typeFilter) return false;
      if (statusFilter === 'Clocked In' && e.todayStatus !== 'clocked_in') return false;
      if (
        statusFilter === 'Not Clocked In' &&
        e.todayStatus !== 'not_clocked_in' &&
        e.todayStatus !== 'clocked_out'
      ) {
        return false;
      }
      if (statusFilter === 'On Leave' && e.todayStatus !== 'on_leave') return false;
      if (statusFilter === 'Late' && e.todayStatus !== 'late') return false;
      if (!q) return true;
      return (
        e.name.toLowerCase().includes(q) ||
        e.code.toLowerCase().includes(q) ||
        e.email.toLowerCase().includes(q) ||
        e.title.toLowerCase().includes(q) ||
        e.branch.toLowerCase().includes(q)
      );
    });
  }, [employees, search, typeFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const pageRows = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  const openEdit = (emp) => {
    setMenuId(null);
    overlay.openEdit(emp);
  };

  const openJobs = (emp) => {
    setMenuId(null);
    setInfoModal({ type: 'jobs', emp });
  };

  const openPay = (emp) => {
    setMenuId(null);
    setInfoModal({ type: 'pay', emp });
  };

  const openDocs = (emp) => {
    const params = new URLSearchParams({ page: 'document-workforce' });
    if (emp?.code) params.set('employeeCode', emp.code);
    if (emp?.dbId) params.set('userId', emp.dbId);
    navigate({ pathname: '/', search: `?${params.toString()}` });
  };

  const handleExport = () => {
    const headers = [
      'Name',
      'Code',
      'Title',
      'Type',
      'Branch',
      'Status',
      'Joining Date',
      'Email',
      'Contact',
      'Compliance',
      'Score',
    ];
    const lines = filtered.map((e) =>
      [
        e.name,
        e.code,
        e.title,
        e.employmentType,
        e.branch,
        e.empStatus,
        e.joiningDate,
        e.email,
        e.contact,
        e.compliancePct,
        e.score,
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(','),
    );
    const blob = new Blob([[headers.join(','), ...lines].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `employee-directory-${todayISO()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openAdd = (memberType) => {
    setAddMenuOpen(false);
    overlay.openAdd({ type: memberType });
  };

  const statusLabel = {
    clocked_in: 'Clocked In',
    clocked_out: 'Clocked Out',
    not_clocked_in: 'Not Clocked In',
    on_leave: 'On Leave',
    late: 'Late',
    active: 'Employed',
    inactive: 'Inactive',
  };

  if (showAdd) {
    return (
      <div className="ed-page">
        <AddMember
          memberType={addType}
          onClose={() => overlay.closeOverlay()}
          onMemberAdded={() => {
            overlay.closeOverlay();
            load();
          }}
        />
      </div>
    );
  }

  return (
    <div className="ed-page">
      <div className="ed-header">
        <div>
          <h1>Employee Directory</h1>
          <p>
            {view === 'list'
              ? `${stats.total} total employees · ${stats.clockedIn} currently clocked in`
              : 'Manage and monitor all employees across your organisation.'}
          </p>
        </div>
        <div className="ed-header-actions">
          <button type="button" className="ed-btn-outline" onClick={handleExport}>
            <FiDownload size={16} /> Export
          </button>
          <div className="ed-add-wrap" ref={addMenuRef}>
            <button
              type="button"
              className="ed-btn-primary ed-add-btn"
              onClick={() => setAddMenuOpen((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={addMenuOpen}
            >
              <FiPlus size={16} /> Add Employee
              <FiChevronDown size={15} className="ed-add-caret" />
            </button>
            {addMenuOpen && (
              <div className="ed-add-menu" role="menu">
                {ADD_MEMBER_TYPES.map((opt) => (
                  <button
                    key={opt.type}
                    type="button"
                    role="menuitem"
                    onClick={() => openAdd(opt.type)}
                  >
                    <span className="ed-add-menu-label">{opt.label}</span>
                    <span className="ed-add-menu-hint">{opt.hint}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {view === 'grid' ? (
        <div className="ed-stats-row">
          <div className="ed-stats-text">
            <span>{stats.total} Total</span>
            <span className="ed-stat-green">{stats.clockedIn} Clocked In</span>
            <span className="ed-stat-orange">{stats.onLeave} On Leave</span>
            <span className="ed-stat-red">{stats.notClockedIn} Not Clocked In</span>
            <span className="ed-stat-amber">{stats.late} Late</span>
          </div>
          <div className="ed-view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={view === 'grid' ? 'active' : ''}
              onClick={() => setView('grid')}
            >
              <FiGrid size={14} /> Grid
            </button>
            <button
              type="button"
              className={view === 'list' ? 'active' : ''}
              onClick={() => setView('list')}
            >
              <FiList size={14} /> List
            </button>
          </div>
        </div>
      ) : (
        <div className="ed-list-toolbar-top">
          <div className="ed-view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={view === 'grid' ? 'active' : ''}
              onClick={() => setView('grid')}
            >
              <FiGrid size={14} /> Grid
            </button>
            <button
              type="button"
              className={view === 'list' ? 'active' : ''}
              onClick={() => setView('list')}
            >
              <FiList size={14} /> List
            </button>
          </div>
        </div>
      )}

      <div className="ed-filters">
        <div className="ed-search">
          <FiSearch size={16} />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder={view === 'list' ? 'Search employees...' : 'Search employee...'}
          />
        </div>
        <div className="ed-pills">
          {['All', 'Full-time', 'Casual', 'Contractor'].map((t) => (
            <button
              key={t}
              type="button"
              className={typeFilter === t ? 'active' : ''}
              onClick={() => {
                setTypeFilter(t);
                setPage(1);
              }}
            >
              {t}
            </button>
          ))}
        </div>
        {view === 'grid' && (
          <div className="ed-pills">
            {['All', 'Clocked In', 'Not Clocked In', 'On Leave', 'Late'].map((t) => (
              <button
                key={t}
                type="button"
                className={statusFilter === t ? 'active' : ''}
                onClick={() => {
                  setStatusFilter(t);
                  setPage(1);
                }}
              >
                {t}
              </button>
            ))}
          </div>
        )}
        <button type="button" className="ed-more-filters">
          <FiFilter size={15} /> {view === 'list' ? 'Filters' : 'More Filters'}
        </button>
      </div>

      <div className="ed-showing">
        Showing {pageRows.length} of {filtered.length} employees
      </div>

      {loading ? (
        <div className="ed-empty">Loading employees...</div>
      ) : filtered.length === 0 ? (
        <div className="ed-empty">No employees match your filters.</div>
      ) : view === 'grid' ? (
        <div className="ed-grid">
          {pageRows.map((emp) => (
            <article key={emp.id} className="ed-card">
              <div className="ed-card-top">
                <div
                  className="ed-avatar"
                  style={emp.photo ? undefined : { background: emp.avatar }}
                >
                  {emp.photo ? (
                    <img src={emp.photo} alt="" />
                  ) : (
                    initials(emp.name)
                  )}
                </div>
                <div className="ed-card-identity">
                  <h3>{emp.name}</h3>
                  <p>{emp.title}</p>
                </div>
              </div>
              <div className="ed-card-badges">
                {/* Account inactive / leave only — hide vague "Active" employment pill */}
                {emp.empStatus === 'inactive' || emp.empStatus === 'on_leave' ? (
                  <span className={`ed-pill emp-${emp.empStatus}`}>
                    <i /> {statusLabel[emp.empStatus]}
                  </span>
                ) : null}
                <span className={`ed-pill today-${emp.todayStatus}`}>
                  <i /> {statusLabel[emp.todayStatus]}
                </span>
              </div>
              <ul className="ed-card-meta">
                <li>
                  <FiHome /> {emp.branch}
                </li>
                <li>
                  <FiBriefcase /> {emp.employmentType}
                </li>
                <li>
                  <FiCalendar /> {emp.joiningDate}
                </li>
                <li>
                  <FiMapPin /> {emp.site}
                </li>
                <li>
                  <FiPhone /> {emp.contact}
                </li>
                <li>
                  <FiMail /> {emp.email}
                </li>
              </ul>
              <div className="ed-card-metrics">
                <div className="ed-bars">
                  <div className="ed-bar-row">
                    <span>Documents</span>
                    <span>{emp.docsPct}%</span>
                  </div>
                  <div className="ed-bar-track">
                    <div
                      className={`ed-bar-fill ${pctColor(emp.docsPct)}`}
                      style={{ width: `${Math.min(100, emp.docsPct)}%` }}
                    />
                  </div>
                  <div className="ed-bar-row">
                    <span>Compliance</span>
                    <span>{emp.compliancePct}%</span>
                  </div>
                  <div className="ed-bar-track">
                    <div
                      className={`ed-bar-fill ${pctColor(emp.compliancePct)}`}
                      style={{ width: `${Math.min(100, emp.compliancePct)}%` }}
                    />
                  </div>
                </div>
                <div className={`ed-score ${pctColor(emp.score)}`}>
                  <svg viewBox="0 0 36 36" aria-hidden>
                    <path
                      className="ed-score-bg"
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path
                      className="ed-score-fg"
                      strokeDasharray={`${emp.score}, 100`}
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                  </svg>
                  <div className="ed-score-label">
                    <strong>{emp.score}</strong>
                    <span>Score</span>
                  </div>
                </div>
              </div>
              <div className="ed-card-actions">
                <button
                  type="button"
                  title="Employee profile"
                  onClick={() => openEdit(emp)}
                >
                  <FiUser />
                </button>
                <button
                  type="button"
                  title="Edit"
                  onClick={() => openEdit(emp)}
                >
                  <FiEdit2 />
                </button>
                <button type="button" title="Jobs" onClick={() => openJobs(emp)}>
                  <FiBriefcase />
                </button>
                <button
                  type="button"
                  title="Docs"
                  onClick={() => openDocs(emp)}
                >
                  <FiFileText />
                </button>
                <button type="button" title="Pay" onClick={() => openPay(emp)}>
                  <FiDollarSign />
                </button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="ed-table-wrap">
          <table className="ed-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Role / Branch</th>
                <th>Type</th>
                <th>Joining Date</th>
                <th>Status</th>
                <th>Compliance</th>
                <th>Performance</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((emp) => (
                <tr key={emp.id}>
                  <td>
                    <div className="ed-emp-cell">
                      <div
                        className="ed-avatar sm"
                        style={emp.photo ? undefined : { background: emp.avatar }}
                      >
                        {emp.photo ? (
                          <img src={emp.photo} alt="" />
                        ) : (
                          initials(emp.name)
                        )}
                      </div>
                      <div>
                        <strong>{emp.name}</strong>
                        <span>{emp.code}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="ed-role-cell">
                      <strong>{emp.title}</strong>
                      <span>{emp.branch}</span>
                    </div>
                  </td>
                  <td>{emp.employmentType}</td>
                  <td>
                    <div className="ed-role-cell">
                      <strong>{emp.joiningDate}</strong>
                      <span>{emp.site !== '—' ? emp.site : emp.shift}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`ed-badge today-${emp.todayStatus}`}>
                      {statusLabel[emp.todayStatus]}
                    </span>
                  </td>
                  <td>
                    <div className="ed-compliance-cell">
                      <div className="ed-bar-track">
                        <div
                          className={`ed-bar-fill ${pctColor(emp.compliancePct)}`}
                          style={{ width: `${Math.min(100, emp.compliancePct)}%` }}
                        />
                      </div>
                      <span>{emp.compliancePct}%</span>
                    </div>
                  </td>
                  <td>
                    <span className="ed-perf">
                      <FiStar /> {emp.score}%
                    </span>
                  </td>
                  <td>
                    <div className="ed-row-actions">
                      <button
                        type="button"
                        title="Employee profile"
                        onClick={() => openEdit(emp)}
                      >
                        <FiUser />
                      </button>
                      <button
                        type="button"
                        title="Edit"
                        onClick={() => openEdit(emp)}
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        type="button"
                        title="Docs"
                        onClick={() => openDocs(emp)}
                      >
                        <FiFileText />
                      </button>
                      <div className="ed-more">
                        <button
                          type="button"
                          title="More"
                          onClick={() => setMenuId(menuId === emp.id ? null : emp.id)}
                        >
                          <FiMoreHorizontal />
                        </button>
                        {menuId === emp.id && (
                          <div className="ed-more-menu">
                            <button type="button" onClick={() => openEdit(emp)}>
                              Profile
                            </button>
                            <button type="button" onClick={() => openJobs(emp)}>
                              Jobs
                            </button>
                            <button type="button" onClick={() => openPay(emp)}>
                              Salary
                            </button>
                            <button type="button" onClick={() => openDocs(emp)}>
                              Documents
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="ed-footer">
          <span>
            Showing {(pageSafe - 1) * PAGE_SIZE + 1}–
            {Math.min(pageSafe * PAGE_SIZE, filtered.length)} of {filtered.length} employees
          </span>
          <div className="ed-pager">
            <button
              type="button"
              disabled={pageSafe <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .slice(0, 5)
              .map((n) => (
                <button
                  key={n}
                  type="button"
                  className={n === pageSafe ? 'active' : ''}
                  onClick={() => setPage(n)}
                >
                  {n}
                </button>
              ))}
            <button
              type="button"
              disabled={pageSafe >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {editMember && (
        <EditMemberModal
          member={editMember}
          onClose={() => overlay.closeOverlay()}
          onSaved={() => {
            overlay.closeOverlay();
            load();
          }}
        />
      )}

      {infoModal?.emp && (
        <div className="ed-info-overlay" onClick={() => setInfoModal(null)}>
          <div
            className="ed-info-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="ed-info-header">
              <div>
                <h2>
                  {infoModal.type === 'jobs' ? 'Jobs' : 'Salary & Pay'}
                </h2>
                <p>
                  {infoModal.emp.name} · {infoModal.emp.code}
                </p>
              </div>
              <button
                type="button"
                className="ed-info-close"
                onClick={() => setInfoModal(null)}
              >
                ×
              </button>
            </div>

            {infoModal.type === 'jobs' ? (
              <div className="ed-info-body">
                <div className="ed-info-grid">
                  <div>
                    <span>Role</span>
                    <strong>{infoModal.emp.title || '—'}</strong>
                  </div>
                  <div>
                    <span>Level</span>
                    <strong>{infoModal.emp.jobTitle || '—'}</strong>
                  </div>
                  <div>
                    <span>Employment type</span>
                    <strong>{infoModal.emp.employmentType || '—'}</strong>
                  </div>
                  <div>
                    <span>Type of work</span>
                    <strong>{infoModal.emp.typeOfWork || '—'}</strong>
                  </div>
                  <div>
                    <span>Branch</span>
                    <strong>{infoModal.emp.branch || '—'}</strong>
                  </div>
                  <div>
                    <span>Current site</span>
                    <strong>{infoModal.emp.site || '—'}</strong>
                  </div>
                  <div>
                    <span>Today shift</span>
                    <strong>{infoModal.emp.shift || '—'}</strong>
                  </div>
                </div>

                <h3 className="ed-info-subtitle">Assigned jobs</h3>
                {(infoModal.emp.jobs || []).length === 0 ? (
                  <p className="ed-info-empty">No active jobs assigned.</p>
                ) : (
                  <ul className="ed-info-jobs">
                    {infoModal.emp.jobs.map((job) => (
                      <li key={job.key}>
                        <div className="ed-info-job-top">
                          <strong>{job.title}</strong>
                          <span>{job.role}</span>
                        </div>
                        <div className="ed-info-job-meta">
                          <span>Client: {job.client}</span>
                          <span>Location: {job.location}</span>
                          <span>Shift: {job.shift}</span>
                          {job.date ? <span>Date: {job.date}</span> : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <div className="ed-info-body">
                <div className="ed-info-grid">
                  <div>
                    <span>Hourly rate</span>
                    <strong>{formatMoney(infoModal.emp.perHourRate)}</strong>
                  </div>
                  <div>
                    <span>Salary term</span>
                    <strong>{infoModal.emp.salaryTerm || '—'}</strong>
                  </div>
                  <div>
                    <span>Pay type</span>
                    <strong>{infoModal.emp.payType || '—'}</strong>
                  </div>
                  <div>
                    <span>Employment type</span>
                    <strong>{infoModal.emp.employmentType || '—'}</strong>
                  </div>
                  <div>
                    <span>Payday</span>
                    <strong>{infoModal.emp.paydayDay || '—'}</strong>
                  </div>
                  <div>
                    <span>Pay time</span>
                    <strong>{infoModal.emp.paydayTime || '—'}</strong>
                  </div>
                  <div>
                    <span>Role</span>
                    <strong>{infoModal.emp.title || '—'}</strong>
                  </div>
                  <div>
                    <span>Level</span>
                    <strong>{infoModal.emp.jobTitle || '—'}</strong>
                  </div>
                  <div>
                    <span>Joining date</span>
                    <strong>{infoModal.emp.joiningDate || '—'}</strong>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
